// similar to `queryOptions` --> `.withProto` or `.withPolling`

import { mergeCallbackOptionsWithPick } from "@mirage-web/utils/merge-callbacks";
import {
  DefaultError,
  InvalidateOptions,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  queryOptions,
  skipToken,
  Updater,
  useQuery,
  UseQueryOptions,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { z } from "zod";

import { NetworkingLayerConfig } from "../../config";
import { extractVariables, makeDefaultQueryFetcher } from "../../default-fetcher";
import { ExtractParamsFromPath } from "../../requester";
import { mergeQueryKeysWithParams } from "../query-key";
import { CallerCombinedVariables, QueryDefinitionOptions, QueryFetcherOptions } from "../types";

import { createQueryInfiniteMixin } from "./pagination";
import { createQueryPollingMixin } from "./polling";

type QueryTriggerOptions<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  TransformedResult,
> = QueryFetcherOptions<TransformedResult> & CallerCombinedVariables<Path, ParamsSchema>;

interface UseQueryHookCallerOptions<
  TQueryKey extends QueryKey,
  ResponseSchema extends z.ZodTypeAny,
  TransformedResult,
>
  extends
    Omit<QueryFetcherOptions<TransformedResult>, "onSuccess" | "onError">,
    Omit<
      UseQueryOptions<z.infer<ResponseSchema>, DefaultError, TransformedResult, TQueryKey>,
      "queryKey" | "queryFn"
    > {}

type ParamsOverload<
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  WithoutParams,
  WithParams,
> = ParamsSchema extends z.ZodVoid
  ? ExtractParamsFromPath<Path> extends Record<string, never>
    ? WithoutParams
    : WithParams
  : WithParams;

export type QueryBaseMixin<
  TQueryKey extends QueryKey,
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  TransformedResult extends object,
> = ReturnType<
  typeof createQueryBaseMixin<TQueryKey, Path, ParamsSchema, ResponseSchema, TransformedResult>
>;

export function createQueryBaseMixin<
  TQueryKey extends QueryKey,
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  TransformedResult extends object = z.infer<ResponseSchema>,
>(
  config: NetworkingLayerConfig,
  definitionOptions: QueryDefinitionOptions<
    TQueryKey,
    Path,
    ParamsSchema,
    ResponseSchema,
    TransformedResult
  >
) {
  const definitionHasParams =
    definitionOptions.paramsSchema || definitionOptions.path.includes(":");

  function pickParamsOverload<WithoutParams, WithParams>(
    withoutParams: WithoutParams,
    withParams: WithParams
  ) {
    return (definitionHasParams ? withParams : withoutParams) as ParamsOverload<
      Path,
      ParamsSchema,
      WithoutParams,
      WithParams
    >;
  }

  function makeFetcher(
    {
      params,
      pathParams,
    }: { params?: z.infer<ParamsSchema>; pathParams?: ExtractParamsFromPath<Path> },
    callerOptions: QueryFetcherOptions<TransformedResult> = {}
  ) {
    const { onError, onSuccess } = mergeCallbackOptionsWithPick(
      ["onError", "onSuccess"],
      definitionOptions,
      callerOptions
    );

    return makeDefaultQueryFetcher(config, {
      ...definitionOptions,
      ...callerOptions,
      headers: {
        ...definitionOptions.headers,
        ...callerOptions.headers,
      },
      onError,
      onSuccess,
      extractParams: () => params ?? {},
      extractPathParams: () => pathParams ?? {},
    });
  }

  function queryOptionsWithoutParams(
    callerOptions: Omit<
      UseQueryHookCallerOptions<TQueryKey, ResponseSchema, TransformedResult>,
      "initialData"
    > = {}
  ) {
    return queryOptions({
      queryKey: definitionOptions.queryKey,
      queryFn: makeFetcher({}, callerOptions),
      ...callerOptions,
    });
  }

  function queryOptionsWithParams(
    combinedParams: CallerCombinedVariables<Path, ParamsSchema> | typeof skipToken,
    callerOptions: Omit<
      UseQueryHookCallerOptions<TQueryKey, ResponseSchema, TransformedResult>,
      "initialData"
    > = {}
  ) {
    const params =
      combinedParams === skipToken
        ? {}
        : definitionOptions.paramsSchema?.parse(
            "params" in combinedParams ? combinedParams.params : undefined
          );

    const pathParams =
      combinedParams !== skipToken && "pathParams" in combinedParams
        ? combinedParams.pathParams
        : undefined;

    return queryOptions({
      queryKey: mergeQueryKeysWithParams(
        definitionOptions.queryKey,
        extractVariables(combinedParams === skipToken ? {} : combinedParams)
      ),
      queryFn: makeFetcher({ params, pathParams }, callerOptions),
      enabled: combinedParams !== skipToken,
      ...callerOptions,
    });
  }

  function useQueryHook(
    callerOptions: UseQueryHookCallerOptions<TQueryKey, ResponseSchema, TransformedResult> = {}
  ) {
    return useQuery({
      ...queryOptionsWithoutParams(callerOptions),
      initialData: callerOptions.initialData,
    });
  }

  function useQueryHookWithParams(
    combinedParams: CallerCombinedVariables<Path, ParamsSchema> | typeof skipToken,
    callerOptions: UseQueryHookCallerOptions<TQueryKey, ResponseSchema, TransformedResult> = {}
  ) {
    return useQuery({
      ...queryOptionsWithParams(combinedParams, callerOptions),
      initialData: callerOptions.initialData,
    });
  }

  function useSuspenseQueryHook(
    callerOptions: UseQueryHookCallerOptions<TQueryKey, ResponseSchema, TransformedResult> = {}
  ) {
    return useSuspenseQuery({
      ...queryOptionsWithoutParams(callerOptions),
      initialData: callerOptions.initialData,
    });
  }

  function useSuspenseQueryHookWithParams(
    combinedParams: CallerCombinedVariables<Path, ParamsSchema> | typeof skipToken,
    callerOptions: UseQueryHookCallerOptions<TQueryKey, ResponseSchema, TransformedResult> = {}
  ) {
    return useSuspenseQuery({
      ...queryOptionsWithParams(combinedParams, callerOptions),
      initialData: callerOptions.initialData,
    });
  }

  async function invalidateQueryWithParams(
    queryClient: QueryClient,
    combinedParams: CallerCombinedVariables<Path, ParamsSchema> | typeof skipToken,
    filters?: Omit<InvalidateQueryFilters, "queryKey" | "exact">,
    options?: InvalidateOptions
  ) {
    const { queryKey } = queryOptionsWithParams(combinedParams);
    await queryClient.invalidateQueries({ queryKey, exact: true, ...filters }, options);
  }

  async function invalidateQueryWithoutParams(
    queryClient: QueryClient,
    filters?: Omit<InvalidateQueryFilters, "queryKey" | "exact">,
    options?: InvalidateOptions
  ) {
    const { queryKey } = queryOptionsWithoutParams();
    await queryClient.invalidateQueries({ queryKey, exact: true, ...filters }, options);
  }

  async function invalidateAllQueries(
    queryClient: QueryClient,
    filters?: Omit<InvalidateQueryFilters, "queryKey" | "exact">,
    options?: InvalidateOptions
  ) {
    const { queryKey } = queryOptionsWithoutParams();
    await queryClient.invalidateQueries({ queryKey, exact: false, ...filters }, options);
  }

  function setQueryDataWithParams(
    queryClient: QueryClient,
    combinedParams: CallerCombinedVariables<Path, ParamsSchema>,
    updater: Updater<TransformedResult | undefined, TransformedResult | undefined>
  ) {
    const { queryKey } = queryOptionsWithParams(combinedParams);
    queryClient.setQueryData<TransformedResult>(queryKey, updater);
  }

  function setQueryDataWithoutParams(
    queryClient: QueryClient,
    updater: Updater<TransformedResult | undefined, TransformedResult | undefined>
  ) {
    const { queryKey } = queryOptionsWithoutParams();
    queryClient.setQueryData<TransformedResult>(queryKey, updater);
  }

  function setAllQueriesData(
    queryClient: QueryClient,
    updater: Updater<TransformedResult | undefined, TransformedResult | undefined>
  ) {
    queryClient.setQueriesData<TransformedResult>(
      { queryKey: definitionOptions.queryKey },
      updater
    );
  }

  function getQueryDataWithParams(
    queryClient: QueryClient,
    combinedParams: CallerCombinedVariables<Path, ParamsSchema>
  ) {
    const { queryKey } = queryOptionsWithParams(combinedParams);
    return queryClient.getQueryData<TransformedResult>(queryKey);
  }

  function getQueryDataWithoutParams(queryClient: QueryClient) {
    const { queryKey } = queryOptionsWithoutParams();
    return queryClient.getQueryData<TransformedResult>(queryKey);
  }

  async function trigger(
    callerOptions: QueryTriggerOptions<Path, ParamsSchema, TransformedResult>
  ) {
    const params = "params" in callerOptions ? callerOptions.params : undefined;
    const pathParams = "pathParams" in callerOptions ? callerOptions.pathParams : undefined;
    const fetcher = makeFetcher({ params, pathParams }, callerOptions);

    // these props are mostly a result of the fetcher originally being built with
    // useQuery in mind
    return fetcher({
      queryKey: definitionOptions.queryKey,
      signal: callerOptions.signal ?? new AbortController().signal,
      meta: undefined,

      // make ts happy, the trigger function will not actually use the
      // client though
      client: config.queryClient,
    });
  }

  const { withPolling } = createQueryPollingMixin(config, definitionOptions);
  const { withPagination } = createQueryInfiniteMixin(config, definitionOptions);

  return {
    useQuery: pickParamsOverload(useQueryHook, useQueryHookWithParams),
    useSuspenseQuery: pickParamsOverload(useSuspenseQueryHook, useSuspenseQueryHookWithParams),
    queryOptions: pickParamsOverload(queryOptionsWithoutParams, queryOptionsWithParams),
    invalidateQuery: pickParamsOverload(invalidateQueryWithoutParams, invalidateQueryWithParams),
    invalidateAllQueries,
    setQueryData: pickParamsOverload(setQueryDataWithoutParams, setQueryDataWithParams),
    setAllQueriesData,
    getQueryData: pickParamsOverload(getQueryDataWithoutParams, getQueryDataWithParams),
    trigger,
    withPolling,
    withPagination,
    queryKey: definitionOptions.queryKey,
  };
}
